import { Badge, Button, Code, Flex, Grid, Modal, Text, Textarea } from "@mantine/core";
import { useApiUrl, useCustomMutation, useShow } from "@refinedev/core";
import {
  EditButton,
  ListButton,
  RefreshButton,
  Show,
} from "@refinedev/mantine";
import { IconRocket } from "@tabler/icons";
import { DetailsCard } from "components/common/show/DetailsPair";
import { History } from "components/common/show/History";
import { TeaAddonOperation, TeaAddonRenewalRun } from "entities/tea_addon_renewal_run";
import { useEffect, useState } from "react";
import { formatDateTime } from "utils/dates";
import { TeaAddonOperations } from "./TeaAddonOperations";

type OperationResponse = {
  success: boolean;
  error?: string;
};

export const TeaAddonRenewalRunShow = () => {
  const [isErrorOpen, setErrorOpen] = useState(false);
  const [errorMessage] = useState<string | null>(null);
  const [isNoteOpen, setNoteOpen] = useState<boolean>(false);
  const [selectedOperation, setSelectedOperation] =
    useState<TeaAddonOperation | null>(null);
  const [operationNote, setOperationNote] = useState<string>("");
  const { queryResult } = useShow<TeaAddonRenewalRun>();
  const { data, isLoading, refetch } = queryResult;
  const tea_addon_renewal_run = data?.data;
  const apiUrl = useApiUrl();

  const { mutate: updateOperationNote, isLoading: isUpdatingOperationNote } =
    useCustomMutation<OperationResponse>();

  const { mutate: launchRun, isLoading: isLaunchingRun } =
    useCustomMutation<OperationResponse>();

  useEffect(() => {
    setOperationNote(selectedOperation?.notes || "");
  }, [selectedOperation?.id, selectedOperation?.notes]);

  if (!tea_addon_renewal_run) return <div />;

  return (
    <Show
      isLoading={isLoading}
      headerButtons={
        <Flex gap="sm">
          <ListButton />
          <EditButton />
          <RefreshButton />
          <Button
            leftIcon={<IconRocket />}
            color="orange"
            disabled={isLaunchingRun}
            onClick={() =>
              tea_addon_renewal_run &&
              launchRun(
                {
                  url: `${apiUrl}/tea_addon_renewal_runs/${tea_addon_renewal_run.id}/launch`,
                  method: "post",
                  values: {},
                },
                {
                  onSuccess: () => refetch(),
                  onError: () => refetch(),
                },
              )
            }
          >
            Launch
          </Button>
        </Flex>
      }
    >
      <Modal
        opened={isErrorOpen}
        onClose={() => setErrorOpen(false)}
        title="Error"
        size="80%"
      >
        <Text color="red">
          <pre>{errorMessage}</pre>
        </Text>
      </Modal>
      <Modal
        opened={isNoteOpen}
        onClose={() => {
          setNoteOpen(false);
          setSelectedOperation(null);
        }}
        size="lg"
        title="Notes"
      >
        <Textarea
          value={operationNote}
          onChange={(e) => setOperationNote(e.target.value)}
        />
        <Button
          mt="lg"
          disabled={isUpdatingOperationNote}
          onClick={() =>
            selectedOperation?.id &&
            updateOperationNote(
              {
                url: `${apiUrl}/tea_addon_operations/${selectedOperation.id}`,
                method: "patch",
                values: { content: operationNote },
              },
              {
                onSuccess: () => {
                  setNoteOpen(false);
                  setSelectedOperation(null);
                  refetch();
                },
              },
            )
          }
        >
          Save
        </Button>
      </Modal>
      <Grid>
        <Grid.Col span={8}>
          <Flex gap="sm" direction="column">
            <DetailsCard title="Operations">
              <TeaAddonOperations
                tea_addon_operations={
                  tea_addon_renewal_run.tea_addon_operations
                }
                onOperation={refetch}
              />
            </DetailsCard>
            <DetailsCard title="Notes">
              {tea_addon_renewal_run.notes}
            </DetailsCard>
            <History<TeaAddonRenewalRun>
              resources={"tea_addon_renewal_runs"}
              entityId={tea_addon_renewal_run.id}
            />
          </Flex>
        </Grid.Col>
        <Grid.Col span={4}>
          <Flex direction="column" gap="sm">
            <DetailsCard title="ID">
              <Text>{tea_addon_renewal_run.id}</Text>
            </DetailsCard>
            <DetailsCard title="Run Type">
              <Badge color={tea_addon_renewal_run.run_type === "dunning" ? "orange" : "blue"}>
                {tea_addon_renewal_run.run_type?.toUpperCase() || "—"}
              </Badge>
            </DetailsCard>
            <DetailsCard title="Range Start At">
              {(() => {
                const formatted = formatDateTime(tea_addon_renewal_run.range_start_at);
                return formatted ? (
                  <Text size="sm">{formatted}</Text>
                ) : (
                  <Badge color="gray" variant="light">—</Badge>
                );
              })()}
            </DetailsCard>
            <DetailsCard title="Range End At">
              {(() => {
                const formatted = formatDateTime(tea_addon_renewal_run.range_end_at);
                return formatted ? (
                  <Text size="sm">{formatted}</Text>
                ) : (
                  <Badge color="gray" variant="light">—</Badge>
                );
              })()}
            </DetailsCard>
            <DetailsCard title="Started At">
              {(() => {
                const formatted = formatDateTime(tea_addon_renewal_run.started_at);
                return formatted ? (
                  <Text size="sm">{formatted}</Text>
                ) : (
                  <Badge color="gray" variant="light">—</Badge>
                );
              })()}
            </DetailsCard>
            <DetailsCard title="Ended At">
              {(() => {
                const formatted = formatDateTime(tea_addon_renewal_run.ended_at);
                return formatted ? (
                  <Text size="sm">{formatted}</Text>
                ) : (
                  <Badge color="gray" variant="light">—</Badge>
                );
              })()}
            </DetailsCard>
          </Flex>
        </Grid.Col>
      </Grid>
    </Show>
  );
};
